/**
 * Clip and body-variant metadata for the rigged-duelist animation post.
 *
 * Generated from the exploration artifact that produced the sheets. The sheet
 * files themselves live in `public/images/rigged-duelist-animations/` as
 * `{variant}-{clip}.webp`, each a `columns x rows` grid of 140px cells
 * sampled at 60 fps.
 *
 * Every number here — frame counts, deltas, flag counts — comes from the
 * verification passes described in the article. Do not hand-edit them.
 */

/** Side of one square cell in a sheet, in source pixels. */
export const RIGGED_DUELIST_CELL_SIZE = 140;

/** Rate the clips were sampled at. Playback rate is a viewer control. */
export const RIGGED_DUELIST_SOURCE_FPS = 60;

export type RiggedDuelistVariant = {
  /** Matches the `{variant}` half of a sheet filename. */
  id: string;
  label: string;
};

export type RiggedDuelistClip = {
  /** Matches the `{clip}` half of a sheet filename. */
  id: string;
  title: string;
  description: string;
  frames: number;
  columns: number;
  rows: number;
  /** Cyclic clips are checked across the wrap-around frame pair too. */
  loops: boolean;
  /** Mean per-frame pixel change, 0–1. */
  meanFrameDelta: number;
  /** Largest change between any neighbouring pair, 0–1. */
  maxFrameDelta: number;
  /** Change across the loop seam, or null for one-shot clips. */
  loopSeamDelta: number | null;
  /** Frames flagged by the rendered-pixel pass. */
  pixelFlags: number;
  /** Frames flagged by the analytic skeleton pass. */
  jointFlags: number;
};

export const RIGGED_DUELIST_VARIANTS: RiggedDuelistVariant[] = [
  {
    "id": "intact",
    "label": "Intact"
  },
  {
    "id": "missingRightHand",
    "label": "No right hand"
  },
  {
    "id": "missingRightLeg",
    "label": "No right leg"
  }
];

export const RIGGED_DUELIST_CLIPS: RiggedDuelistClip[] = [
  {
    "id": "idle",
    "title": "Idle",
    "description": "Standing and breathing over 3.4 s. Deliberately no lateral weight shift and no leg motion — either one makes it read as a very slow walk.",
    "frames": 204,
    "columns": 15,
    "rows": 14,
    "loops": true,
    "meanFrameDelta": 0.01055115710769733,
    "maxFrameDelta": 0.022138607971445567,
    "loopSeamDelta": 0.006567519333729923,
    "pixelFlags": 0,
    "jointFlags": 0
  },
  {
    "id": "walk",
    "title": "Walk cycle",
    "description": "Two-step cycle, knees flexing on the trailing leg, sword carried forward at the ready rather than hanging.",
    "frames": 54,
    "columns": 8,
    "rows": 7,
    "loops": true,
    "meanFrameDelta": 0.03391149998898363,
    "maxFrameDelta": 0.043378941106484235,
    "loopSeamDelta": 0.03400356930398572,
    "pixelFlags": 2,
    "jointFlags": 0
  },
  {
    "id": "chop",
    "title": "Chop",
    "description": "Overhead chop — the same arm motion as the stab with the blade mirrored about the forearm, so it points UP out of the fist. Wrist held at 239–299°.",
    "frames": 53,
    "columns": 8,
    "rows": 7,
    "loops": false,
    "meanFrameDelta": 0.051838591955337944,
    "maxFrameDelta": 0.07830755502676978,
    "loopSeamDelta": null,
    "pixelFlags": 0,
    "jointFlags": 0
  },
  {
    "id": "stab",
    "title": "Stab",
    "description": "Downward stab — the arm lifts and drives back down with the blade held pointing DOWN relative to the forearm. Wrist held at 61–121°.",
    "frames": 53,
    "columns": 8,
    "rows": 7,
    "loops": false,
    "meanFrameDelta": 0.05435263464970484,
    "maxFrameDelta": 0.08175788221296847,
    "loopSeamDelta": null,
    "pixelFlags": 0,
    "jointFlags": 0
  },
  {
    "id": "guard",
    "title": "Guard",
    "description": "Hips drop, stance widens, both arms bring the blade level across the chest.",
    "frames": 31,
    "columns": 6,
    "rows": 6,
    "loops": false,
    "meanFrameDelta": 0.030380279595478888,
    "maxFrameDelta": 0.041442593694229625,
    "loopSeamDelta": null,
    "pixelFlags": 0,
    "jointFlags": 0
  },
  {
    "id": "unsheathe",
    "title": "Draw",
    "description": "Cross-body draw from the far hip. One clip, two states — the sheathed scabbard swaps for the empty one at the instant the blade clears it.",
    "frames": 48,
    "columns": 7,
    "rows": 7,
    "loops": false,
    "meanFrameDelta": 0.028850481602895956,
    "maxFrameDelta": 0.042688875669244494,
    "loopSeamDelta": null,
    "pixelFlags": 2,
    "jointFlags": 0
  },
  {
    "id": "sheathe",
    "title": "Sheathe",
    "description": "The reverse: blade brought across, fed into the scabbard, hand falls away.",
    "frames": 55,
    "columns": 8,
    "rows": 7,
    "loops": false,
    "meanFrameDelta": 0.025338009782536842,
    "maxFrameDelta": 0.03662254610350982,
    "loopSeamDelta": null,
    "pixelFlags": 5,
    "jointFlags": 0
  },
  {
    "id": "idleSheathed",
    "title": "Idle — sheathed",
    "description": "The same breathing idle with the blade stowed and the hand empty.",
    "frames": 204,
    "columns": 15,
    "rows": 14,
    "loops": true,
    "meanFrameDelta": 0.0082263125357222,
    "maxFrameDelta": 0.01928613920285544,
    "loopSeamDelta": 0.003588637715645449,
    "pixelFlags": 0,
    "jointFlags": 0
  },
  {
    "id": "walkSheathed",
    "title": "Walk — sheathed",
    "description": "The same walk cycle with the blade stowed — arms swing freely.",
    "frames": 54,
    "columns": 8,
    "rows": 7,
    "loops": true,
    "meanFrameDelta": 0.0326672285015533,
    "maxFrameDelta": 0.04232450922070196,
    "loopSeamDelta": 0.03380130874479476,
    "pixelFlags": 2,
    "jointFlags": 0
  },
  {
    "id": "hurt",
    "title": "Hurt — light",
    "description": "A light hit: rocked back onto the rear foot and straight back to guard.",
    "frames": 32,
    "columns": 6,
    "rows": 6,
    "loops": false,
    "meanFrameDelta": 0.04841026846539118,
    "maxFrameDelta": 0.07082986317668055,
    "loopSeamDelta": null,
    "pixelFlags": 2,
    "jointFlags": 0
  },
  {
    "id": "hurtHeavy",
    "title": "Hurt — heavy",
    "description": "A heavy hit: driven back a full step, knees buckle, and the recovery takes noticeably longer.",
    "frames": 44,
    "columns": 7,
    "rows": 7,
    "loops": false,
    "meanFrameDelta": 0.052365390202398894,
    "maxFrameDelta": 0.08474866151100535,
    "loopSeamDelta": null,
    "pixelFlags": 1,
    "jointFlags": 7
  },
  {
    "id": "hurtCritical",
    "title": "Hurt — critical",
    "description": "A crushing hit — the only reaction that puts him on the ground. He drops to a knee and has to push back up.",
    "frames": 64,
    "columns": 8,
    "rows": 8,
    "loops": false,
    "meanFrameDelta": 0.04721336978178144,
    "maxFrameDelta": 0.08985276621058894,
    "loopSeamDelta": null,
    "pixelFlags": 2,
    "jointFlags": 8
  },
  {
    "id": "defeat",
    "title": "Defeat",
    "description": "Sinks under his own weight. The hip drop is bounded by what the folding legs give up, so the feet stay on the floor.",
    "frames": 97,
    "columns": 10,
    "rows": 10,
    "loops": false,
    "meanFrameDelta": 0.03043122893119175,
    "maxFrameDelta": 0.038554431885782274,
    "loopSeamDelta": null,
    "pixelFlags": 0,
    "jointFlags": 0
  }
];

/** Total authored frames across every clip, at the 60 fps sampling rate. */
export const RIGGED_DUELIST_TOTAL_FRAMES = 993;

export function riggedDuelistSheetPath(variantId: string, clipId: string) {
  return `/images/rigged-duelist-animations/${variantId}-${clipId}.webp`;
}
